/**
 * AI Assistant — host-side imperative API.
 *
 * Lets any component anywhere in nexilcolab (debts, orders, profile, …)
 * trigger the assistant without holding a ref to the drawer:
 *
 *   const ai = useAiAssistant();
 *
 *   // open the FAB and prefill the input ("ask the bot about X")
 *   ai.ask('נתח את הכרטסת והצע פעולות', {
 *     page: 'customer.debts',
 *     data: { client_id: 335, debt: 4500, recent_invoices: [...] },
 *   });
 *
 *   // just push the page context (so when the user opens the chat manually,
 *   // the bot already knows what they're looking at)
 *   ai.setPageContext({ page: 'customer.profile', data: { client_id: 335 } });
 *
 *   // open the widget without any prefilled prompt
 *   ai.open();
 *
 *   // jump to a specific thread
 *   ai.openThread('uuid-of-thread');
 *
 * Internally these dispatch `window` CustomEvents that AiAssistantDrawer
 * listens to and forwards to the iframe via postMessage. Identical pattern
 * to `wa-open-chat` / `wa-open-bulk` for the WhatsApp drawer.
 *
 * Why events (instead of a context provider)?
 *   - Zero coupling: callers don't have to be inside any provider.
 *   - Works from non-React land (Kotlin/JSDB callbacks, Swal modals, etc).
 *   - Easy to forward to other hosts later (e.g. a separate Tauri window).
 */
import { useCallback, useEffect, useRef } from 'react';

export interface AiPageContext {
    /** Logical screen identifier — e.g. "customer.debts", "orders.dashboard". */
    page?: string;
    /** Short human-friendly summary the bot can echo back. */
    summary?: string;
    /** The actual data the user is looking at (client/invoice/order/...). */
    data?: Record<string, unknown>;
    /** Free-form extras (any other host-app metadata). */
    [key: string]: unknown;
}

export interface AskOptions {
    /** Optional agent override (e.g. "debt_collector"). */
    agentId?: string;
    /** When false, drop the prompt into the input but don't send. Default true. */
    send?: boolean;
    /** Optional page context to attach to this ask (overrides the global one). */
    context?: AiPageContext;
    /** Optional custom event detail extras. */
    [key: string]: unknown;
}

const EVENT_ASK = 'ai-ask';
const EVENT_OPEN = 'ai-open';
const EVENT_OPEN_THREAD = 'ai-open-thread';
const EVENT_OPEN_VIEW = 'ai-open-view';
const EVENT_SET_CONTEXT = 'ai-set-context';
const EVENT_SET_AGENT = 'ai-set-agent';

function _dispatch(name: string, detail: any): void {
    if (typeof window === 'undefined') return;
    try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* */ }
}

export function aiAsk(prompt: string, context?: AiPageContext, opts: AskOptions = {}): void {
    _dispatch(EVENT_ASK, {
        prompt,
        context: context || opts.context,
        agentId: opts.agentId,
        send: opts.send !== false,
    });
}

export function aiOpen(opts: { agentId?: string } = {}): void {
    _dispatch(EVENT_OPEN, { agentId: opts.agentId });
}

export function aiOpenThread(threadId: string): void {
    _dispatch(EVENT_OPEN_THREAD, { threadId });
}

/** Open a specific ChatWidget tab (e.g. `docs-manager` for the drafts drawer). */
export function aiOpenView(
    view: string,
    opts: { draftId?: string; agentId?: string } = {},
): void {
    _dispatch(EVENT_OPEN_VIEW, { view, draftId: opts.draftId, agentId: opts.agentId });
}

/** Shortcut: full-screen documents / drafts manager inside the AI chat iframe. */
export function aiOpenDocsManager(draftId?: string): void {
    aiOpenView('docs-manager', { draftId });
}

/** Shortcut: the Debt Collection center (the "גבייה" process tab). */
export function aiOpenDebtCollection(): void {
    aiOpenView('process:debt');
}

export function aiSetPageContext(context: AiPageContext | null): void {
    _dispatch(EVENT_SET_CONTEXT, { context });
}

export interface AiPageContextHandle {
    update: (context: AiPageContext) => void;
    dispose: () => void;
}

const pageContextStack = new Map<number, { context: AiPageContext; order: number; priority: number }>();
let nextPageContextId = 1;
let nextPageContextOrder = 1;

function publishStackedPageContext(): void {
    let active: { context: AiPageContext; order: number; priority: number } | undefined;
    pageContextStack.forEach((entry) => {
        if (!active || entry.priority > active.priority || (
            entry.priority === active.priority && entry.order > active.order
        )) active = entry;
    });
    aiSetPageContext(active?.context ?? null);
}

/**
 * Registers temporary page context and restores the previous registered
 * context when disposed. Useful for dialogs and other stacked host surfaces.
 */
export function pushAiPageContext(context: AiPageContext, priority = 0): AiPageContextHandle {
    const id = nextPageContextId++;
    let disposed = false;
    pageContextStack.set(id, { context, order: nextPageContextOrder++, priority });
    publishStackedPageContext();

    return {
        update(nextContext: AiPageContext) {
            if (disposed) return;
            pageContextStack.set(id, { context: nextContext, order: nextPageContextOrder++, priority });
            publishStackedPageContext();
        },
        dispose() {
            if (disposed) return;
            disposed = true;
            pageContextStack.delete(id);
            publishStackedPageContext();
        },
    };
}

/** Pushes page context while a React surface is active and cleans it up on unmount. */
export function useAiPageContext(context: AiPageContext | null, active = true, priority = 0): void {
    const handleRef = useRef<AiPageContextHandle | null>(null);

    useEffect(() => {
        if (!active || !context) return undefined;
        handleRef.current = pushAiPageContext(context, priority);
        return () => {
            handleRef.current?.dispose();
            handleRef.current = null;
        };
    }, [active, priority]);

    useEffect(() => {
        if (active && context) handleRef.current?.update(context);
    }, [active, context]);
}

export function aiSetAgent(agentId: string): void {
    _dispatch(EVENT_SET_AGENT, { agentId });
}

export function useAiAssistant() {
    return {
        ask: useCallback(aiAsk, []),
        open: useCallback(aiOpen, []),
        openThread: useCallback(aiOpenThread, []),
        openView: useCallback(aiOpenView, []),
        openDocsManager: useCallback(aiOpenDocsManager, []),
        openDebtCollection: useCallback(aiOpenDebtCollection, []),
        setPageContext: useCallback(aiSetPageContext, []),
        setAgent: useCallback(aiSetAgent, []),
    };
}

/** Internal: event-name constants for the drawer listener. */
export const AI_EVENTS = {
    ASK: EVENT_ASK,
    OPEN: EVENT_OPEN,
    OPEN_THREAD: EVENT_OPEN_THREAD,
    OPEN_VIEW: EVENT_OPEN_VIEW,
    SET_CONTEXT: EVENT_SET_CONTEXT,
    SET_AGENT: EVENT_SET_AGENT,
};
